import { useMemo, useCallback } from "react";
import { scaleBand, scaleLinear } from "@visx/scale";
import { AxisBottom, AxisLeft } from "@visx/axis";
import { GridColumns } from "@visx/grid";
import { Group } from "@visx/group";
import { ParentSize } from "@visx/responsive";
import { useTooltip } from "@visx/tooltip";
import { ChartTooltip } from "../../../shared/components/ChartTooltip/ChartTooltip";
import {
  ChartLegend,
  type LegendItem,
} from "../../../shared/components/ChartLegend/ChartLegend";
import type { OrgAdoptionResponse } from "../../types";
import { formatNumber, formatCompact } from "../../utils/formatters";
import styles from "./OrgAdoptionChart.module.css";

type OrgEntry = OrgAdoptionResponse["orgs"][number];

const MAX_BARS = 15;
const MARGIN = { top: 8, right: 16, bottom: 40, left: 140 };
const ADMIN_COLOR = "#6366f1";
const MEMBER_COLOR = "#a5b4fc";

function truncateLabel(name: string | null | undefined, fallback: string, maxLen = 20): string {
  const label = name ?? fallback;
  return label.length > maxLen ? `${label.slice(0, maxLen)}…` : label;
}

interface TooltipData {
  entry: OrgEntry;
  segment: "admin" | "member";
}

interface InnerChartProps {
  orgs: OrgEntry[];
  width: number;
  height: number;
}

function InnerChart({ orgs, width, height }: InnerChartProps) {
  const {
    tooltipOpen,
    tooltipData,
    tooltipLeft,
    tooltipTop,
    showTooltip,
    hideTooltip,
  } = useTooltip<TooltipData>();

  const innerWidth = width - MARGIN.left - MARGIN.right;
  const innerHeight = height - MARGIN.top - MARGIN.bottom;

  const yScale = useMemo(
    () =>
      scaleBand<string>({
        domain: orgs.map((o) => o.org_id),
        range: [0, innerHeight],
        padding: 0.25,
      }),
    [orgs, innerHeight],
  );

  const xScale = useMemo(
    () =>
      scaleLinear<number>({
        domain: [0, orgs.reduce((m, o) => Math.max(m, o.total_members), 0) * 1.1 || 1],
        range: [0, innerWidth],
        nice: true,
      }),
    [orgs, innerWidth],
  );

  const orgLabelMap = useMemo(
    () => new Map(orgs.map((o) => [o.org_id, truncateLabel(o.org_name, o.org_id)])),
    [orgs],
  );

  const handleMouseOver = useCallback(
    (entry: OrgEntry, segment: "admin" | "member", xPos: number) => {
      const barY = yScale(entry.org_id) ?? 0;
      const barHeight = yScale.bandwidth();
      showTooltip({
        tooltipData: { entry, segment },
        tooltipLeft: MARGIN.left + xPos,
        tooltipTop: MARGIN.top + barY + barHeight / 2,
      });
    },
    [yScale, showTooltip],
  );

  if (innerWidth <= 0 || innerHeight <= 0) return null;

  return (
    <div className={styles.chartWrapper}>
      <svg width={width} height={height} role="img" aria-label="Organisation adoption">
        <Group left={MARGIN.left} top={MARGIN.top}>
          <GridColumns
            scale={xScale}
            height={innerHeight}
            stroke="var(--colours-neutral-100, #f3f4f6)"
          />
          {orgs.map((entry) => {
            const barY = yScale(entry.org_id) ?? 0;
            const barHeight = yScale.bandwidth();
            const adminWidth = xScale(entry.admin_count);
            const memberWidth = xScale(entry.member_count);
            const isHoveredOrg = tooltipData?.entry.org_id === entry.org_id;
            return (
              <Group key={entry.org_id}>
                {/* Admin segment (left) */}
                <rect
                  x={0}
                  y={barY}
                  width={Math.max(adminWidth, 0)}
                  height={barHeight}
                  fill={isHoveredOrg && tooltipData?.segment === "admin" ? "#818cf8" : ADMIN_COLOR}
                  rx={entry.member_count === 0 ? 3 : 0}
                  onMouseMove={() => handleMouseOver(entry, "admin", adminWidth)}
                  onMouseLeave={hideTooltip}
                />
                {/* Member segment (right of admin) */}
                <rect
                  x={Math.max(adminWidth, 0)}
                  y={barY}
                  width={Math.max(memberWidth, 0)}
                  height={barHeight}
                  fill={isHoveredOrg && tooltipData?.segment === "member" ? "#c7d2fe" : MEMBER_COLOR}
                  rx={3}
                  onMouseMove={() => handleMouseOver(entry, "member", adminWidth + memberWidth)}
                  onMouseLeave={hideTooltip}
                />
              </Group>
            );
          })}
          <AxisBottom
            top={innerHeight}
            scale={xScale}
            numTicks={5}
            stroke="var(--colours-neutral-200, #e5e7eb)"
            tickStroke="var(--colours-neutral-200, #e5e7eb)"
            tickFormat={(v) => formatCompact(v as number)}
            tickLabelProps={() => ({
              fill: "var(--colours-neutral-500, #6b7280)",
              fontSize: 11,
              textAnchor: "middle" as const,
            })}
            label="Members"
            labelOffset={20}
            labelProps={{
              fill: "var(--colours-neutral-500, #6b7280)",
              fontSize: 11,
              textAnchor: "middle" as const,
            }}
          />
          <AxisLeft
            scale={yScale}
            stroke="var(--colours-neutral-200, #e5e7eb)"
            tickStroke="var(--colours-neutral-200, #e5e7eb)"
            tickFormat={(v) => orgLabelMap.get(v as string) ?? (v as string)}
            tickLabelProps={() => ({
              fill: "var(--colours-neutral-500, #6b7280)",
              fontSize: 11,
              textAnchor: "end" as const,
              dx: -4,
              dy: 4,
            })}
          />
        </Group>
      </svg>
      {tooltipOpen && tooltipData && (
        <ChartTooltip top={tooltipTop ?? 0} left={tooltipLeft ?? 0}>
          <div className={styles.tooltipContent}>
            <span className={styles.tooltipLabel}>
              {tooltipData.entry.org_name ?? tooltipData.entry.org_id}
            </span>
            <span>
              <strong>{formatNumber(tooltipData.segment === "admin" ? tooltipData.entry.admin_count : tooltipData.entry.member_count)}</strong>{" "}
              {tooltipData.segment === "admin" ? "admins" : "members"}
            </span>
            <span className={styles.tooltipSecondary}>
              {formatNumber(tooltipData.entry.total_members)} total
            </span>
          </div>
        </ChartTooltip>
      )}
    </div>
  );
}

const LEGEND_ITEMS: LegendItem[] = [
  { label: "Admins", color: ADMIN_COLOR },
  { label: "Members", color: MEMBER_COLOR },
];

export interface OrgAdoptionChartProps {
  data: OrgAdoptionResponse;
}

const ROW_HEIGHT = 28;

export function OrgAdoptionChart({ data }: OrgAdoptionChartProps) {
  const sorted = useMemo(
    () =>
      [...data.orgs]
        .sort((a, b) => b.total_members - a.total_members)
        .slice(0, MAX_BARS),
    [data.orgs],
  );

  if (sorted.length === 0) {
    return (
      <div className={styles.emptyState}>No organisation adoption data available.</div>
    );
  }

  const chartHeight = Math.max(sorted.length * ROW_HEIGHT + MARGIN.top + MARGIN.bottom, 200);

  return (
    <div className={styles.container}>
      <ChartLegend items={LEGEND_ITEMS} />
      <ParentSize>
        {({ width }) => (
          <InnerChart orgs={sorted} width={width} height={chartHeight} />
        )}
      </ParentSize>
    </div>
  );
}
